import { Link } from "react-router-dom";
import { Truck, Package, MapPin, Clock } from "lucide-react";

const TODAY = new Date().toLocaleDateString("en-AU", {
  year: "numeric",
  month: "long",
  day: "numeric",
});

const HIGHLIGHTS = [
  {
    icon: MapPin,
    label: "Australia wide",
    desc: "We ship to every Australian state and territory from our Melbourne facility.",
  },
  {
    icon: Clock,
    label: "1 business day dispatch",
    desc: "Orders confirmed before 1pm AEST are dispatched the same day.",
  },
  {
    icon: Truck,
    label: "Express post upgrade",
    desc: "Standard post by default. Upgrade to Express Post at checkout for faster delivery.",
  },
  {
    icon: Package,
    label: "Discreet packaging",
    desc: "Plain, unbranded outer packaging. No RegenX branding on the box.",
  },
];

const SECTIONS = [
  {
    h: "Where we ship",
    p: [
      "RegenX Wellness ships Australia wide — to every state and territory — directly from our Melbourne facility. We do not currently ship internationally.",
    ],
  },
  {
    h: "Dispatch & delivery times",
    p: [
      "Orders are dispatched within one (1) business day of payment confirmation. Bank-transfer orders are dispatched once cleared funds are received.",
      "Standard post delivery estimates from dispatch: Metropolitan Melbourne 2–4 business days; Sydney, Brisbane, Adelaide, Canberra 3–6 business days; Perth, Hobart, Darwin and regional addresses 5–10 business days. Delivery times are indicative and provided by the carrier.",
      "Express Post delivery estimates are typically 1–3 business days nationwide. Express Post can be selected as an upgrade at checkout for an additional fee.",
    ],
  },
  {
    h: "Shipping cost",
    p: [
      "A flat standard shipping fee applies to every order and is calculated at checkout. Express Post is available as a paid upgrade. Bulk and recurring research-account customers can contact us for negotiated rates.",
    ],
  },
  {
    h: "Standard post & no signature",
    p: [
      "Orders are sent via standard untracked post by default. No signature is required on delivery — your order will be left in your mailbox or at the address if it fits, or you may receive a card to collect it from your local post office if it does not.",
      "If you would prefer faster, trackable delivery, select Express Post at checkout.",
    ],
  },
  {
    h: "Storage on arrival",
    p: [
      "Lyophilized peptides remain stable at room temperature during transit. On arrival, store at -20°C or below, protected from light, for long-term storage. Reconstituted peptides should be refrigerated and used within the timeframes appropriate to the compound.",
    ],
  },
  {
    h: "Lost, damaged or undelivered orders",
    p: [
      "If your order arrives damaged or fails to arrive within the expected window, contact us at info@regenxwellness.com.au within 7 days of the expected delivery date. We will work with the carrier to investigate and arrange replacement or refund as appropriate.",
    ],
  },
  {
    h: "Address accuracy",
    p: [
      "Please ensure your shipping address is correct at checkout. We cannot be responsible for orders sent to addresses provided incorrectly. If you spot an error, contact us immediately at info@regenxwellness.com.au — we may be able to amend the address before dispatch.",
    ],
  },
];

export default function Shipping() {
  return (
    <div data-testid="shipping-page" className="animate-in fade-in duration-500">
      <section className="px-6 md:px-12 lg:px-24 pt-20 pb-12 border-b border-border max-w-4xl">
        <p className="overline text-primary mb-5">Legal</p>
        <h1 className="editorial-heading text-5xl md:text-6xl text-secondary leading-[0.95] mb-6">
          Shipping policy
        </h1>
        <p className="text-sm text-muted-foreground">Last updated: {TODAY}</p>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-12 border-b border-border">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {HIGHLIGHTS.map((h, i) => {
            const Icon = h.icon;
            return (
              <div key={i} className="flex flex-col gap-3" data-testid={`shipping-highlight-${i}`}>
                <Icon className="h-5 w-5 text-primary" />
                <p className="overline text-secondary">{h.label}</p>
                <p className="text-sm text-muted-foreground leading-relaxed">{h.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="px-6 md:px-12 lg:px-24 py-16 max-w-4xl space-y-12">
        {SECTIONS.map((s, i) => (
          <div key={i} data-testid={`shipping-section-${i}`}>
            <h2 className="editorial-heading text-2xl md:text-3xl text-secondary mb-4">
              {s.h}
            </h2>
            {s.p.map((para, j) => (
              <p key={j} className="text-base text-muted-foreground leading-relaxed mb-4">
                {para}
              </p>
            ))}
          </div>
        ))}
        <div className="pt-8 border-t border-border">
          <Link
            to="/"
            className="text-xs uppercase tracking-[0.22em] text-secondary hover:text-primary border-b border-secondary pb-1"
            data-testid="shipping-back-home"
          >
            ← Back home
          </Link>
        </div>
      </section>
    </div>
  );
}
